// tslint:disable: no-shadowed-variable
import { format } from "lib/globalize-util.js";
import { checkStatus, fetch } from "./base-actions";
import { mutateAsync, requestAsync } from "lib/api";
import * as queries from "queries/templates-queries";
import { add as snackbarAdd } from "actions/snackbars";
import { TEMPLATE, TEMPLATES } from "constants/action-types";
import { IFetchStoreState } from "reducers/base-reducers";
import ITemplate, { FieldType, ITemplateField, ITemplatePreview } from "constants/template-types";
import iassign from "immutable-assign-v1";
import pick from "lodash/pick";
import convert from "lodash/fp/convert";
import get from "lodash/get";
import some from "lodash/some";
import concat from "lodash/concat";
import lodashAssign from "lodash/assign";
import map from "lodash/map";
import reject from "lodash/reject";

const fp = convert({
    assign: lodashAssign,
    map,
    reject,
});

// immutable assign from lodash with usual syntax
// useful for functional programing concepts
const assign = (a: object) => (b: object) => fp.assign(b, a);

export const fetchTemplate = (id: string) => fetch(TEMPLATE.FETCH, queries.fetchTemplateEndpoint(id));

export const fetchTemplates = () => fetch(TEMPLATES.FETCH, queries.fetchTemplatesEndpoint());

export const createTemplate = (router) => {
    return (dispatch, getState) =>
        dispatch(requestAsync(queries.createTemplateQuery())).then(({ status, body: template }) => {
            if (status === 403) {
                dispatch(snackbarAdd({ message: format("You do not have permission to create a template") }));
            } else if (checkStatus(dispatch, status, template)) {
                router.push(`/brand/${getState().scope.brand._id}/admin/templates/${template.id}`);
            }
        });
};

// updates templates list to avoid additional requests
const changeTemplates = (id: string, changes: Partial<ITemplatePreview>) => {
    return (dispatch, getState) => {
        let templates = getState().templates.payload;
        if (templates) {
            if (changes.default_format) {
                templates = fp.map(assign({ default_format: false }), templates);
            }
            dispatch({
                type: TEMPLATES.UPDATE,
                payload: iassign<ITemplatePreview[], ITemplatePreview, { index: number }>(
                    templates,
                    (templates, context) => templates[context.index],
                    assign(changes),
                    { index: templates.findIndex((value) => value.id === id) },
                ),
            });
        }
    };
};

export const updateTemplate = (id: string, changes: Partial<ITemplate>) => {
    return (dispatch, getState) => {
        const template = getState().template.payload;

        if (template) {
            dispatch({
                type: TEMPLATE.UPDATE,
                payload: iassign<ITemplate>(template, assign(changes)),
            });
        }
        dispatch(changeTemplates(id, changes));
        return dispatch(mutateAsync(queries.updateTemplateQuery(id, changes))).then(({ status, body: template }) => {
            if (checkStatus(dispatch, status, template) && changes.product_id_field) {
                dispatch(changeTemplate(pick(template, "fields")));
            }
        });
    };
};

const changeTemplate = (changes: Partial<ITemplate>) => {
    return (dispatch, getState) => {
        dispatch({
            type: TEMPLATE.UPDATE,
            payload: iassign<ITemplate>(getState().template.payload, assign(changes)),
        });
    };
};

const deleteRequest = (id: string, onDelete: (dispatch, store) => void) => {
    return (dispatch, getStore) =>
        dispatch(mutateAsync(queries.deleteTemplateQuery(id))).then(({ status, body }) => {
            if (status === 403) {
                dispatch(snackbarAdd({ message: format("You do not have permission to delete a template") }));
            } else if (status === 409) {
                dispatch(snackbarAdd({ message: format("You can not remove a default template") }));
            } else if (checkStatus(dispatch, status, body)) {
                dispatch(fetchTemplates());
                onDelete(dispatch, getStore());
            }
        });
};

interface IStore {
    templates: IFetchStoreState<ITemplatePreview[]>;
}

export const deleteTemplateFromList = (id: string) => {
    const onDelete = (dispatch, store: IStore) => {
        dispatch({
            type: TEMPLATES.UPDATE,
            payload: iassign<ITemplatePreview[]>(store.templates.payload, fp.reject({ id })),
        });
    };
    return deleteRequest(id, onDelete);
};

export const deleteTemplate = (id: string, router) => {
    const onDelete = (dispatch, store) => router.push(`/brand/${store.scope.brand._id}/admin/templates`);
    return deleteRequest(id, onDelete);
};

export const updateTemplateRowOrder = (field: string, after: string) => {
    return (dispatch, getState) => {
        const template = getState().template.payload;

        const itemIndex = template.fields.findIndex((value) => value.id === field);
        const dropIndex = after ? template.fields.findIndex((value) => value.id === after) : 0;
        dispatch({
            type: TEMPLATE.UPDATE,
            payload: iassign<ITemplate, ITemplateField[], { itemIndex: number; dropIndex: number }>(
                template,
                (template) => template.fields,
                (fields) => {
                    fields.splice(dropIndex, 0, fields.splice(itemIndex, 1)[0]);
                    return fields;
                },
            ),
        });

        const before = get(getState().template.payload, `fields.${dropIndex - 1}.id`, null);
        dispatch(mutateAsync(queries.updateRowOrderQuery(template.id, { field, before }))).then(({ status, body }) =>
            checkStatus(dispatch, status, body),
        );
    };
};

export const addTemplateRow = (options: { type: FieldType; header: string; field: string }) => {
    return (dispatch, getState) =>
        dispatch(mutateAsync(queries.addRowQuery(getState().template.payload.id, options))).then(
            ({ status, body: field }) => {
                if (checkStatus(dispatch, status, field)) {
                    dispatch({
                        type: TEMPLATE.UPDATE,
                        payload: iassign<ITemplate, ITemplateField[], {}>(
                            getState().template.payload,
                            (template) => template.fields,
                            (fields) => concat(fields, field),
                        ),
                    });
                }
            },
        );
};

export const deleteTemplateRow = (rowId: string) => {
    return (dispatch, getState) => {
        dispatch({
            type: TEMPLATE.UPDATE,
            payload: iassign<ITemplate, ITemplateField[], {}>(
                getState().template.payload,
                (template) => template.fields,
                fp.reject({ id: rowId }),
            ),
        });
        dispatch(mutateAsync(queries.deleteRowQuery(getState().template.payload.id, rowId))).then(({ status, body }) =>
            checkStatus(dispatch, status, body),
        );
    };
};

export const changeTemplateRow = (rowId: string, changes: Partial<ITemplateField>) => {
    return (dispatch, getState) => {
        const template = getState().template.payload;

        dispatch({
            type: TEMPLATE.UPDATE,
            payload: iassign<ITemplate, ITemplateField, { rowIndex: number }>(
                template,
                (template, context) => template.fields[context.rowIndex],
                assign(changes),
                { rowIndex: template.fields.findIndex((value) => value.id === rowId) },
            ),
        });
    };
};

export const updateTemplateRow = (rowId: string, changes: Partial<ITemplateField>) => {
    return (dispatch, getState) => {
        if (some(changes)) {
            dispatch(mutateAsync(queries.updateRowQuery(getState().template.payload.id, rowId, changes))).then(
                ({ status, body }) => checkStatus(dispatch, status, body),
            );
        }
    };
};

// todo: process response when feature is ready to use
export const generateTemplatePreview = (id: string, router) => {
    return (dispatch) => {
        dispatch(requestAsync(queries.generatePreviewQuery(id))).then(({ status, body }) => {
            checkStatus(dispatch, status, body);
        });
    };
};
